// The discharge-plan API and the pure date arithmetic the bed board marker,
// the popup and the Discharge Plan screen all share.

import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environment/environment';
import { ServerTokenService } from '../../services/server-token.service';
import {
  DrawerChoiceSection,
  DrawerFacilitySection,
  DrawerOptionSection,
} from '../../admin/shared/high-risk-content-drawer/drawer-options';
import { easternToday, ISO_DATE_RE } from '../eastern-date';
import { locationText } from '../unit-label';
import { FacilityService } from '../facility.service';
import {
  DischargeBucket,
  DischargeEmailHorizon,
  DischargeFacilityGroup,
  DischargeFacilitySummary,
  DischargeGroup,
  DischargeHorizon,
  DischargePlan,
  DischargePlanByPatientId,
  DischargePlanEmailSettings,
  DischargePlanInput,
  DischargePlanListResponseDto,
  DischargePlanRow,
  DischargePlanRowDto,
  DischargeSort,
  DischargeView,
  GenderClass,
  PayerClass,
} from './discharge-plan.interface';

export interface DischargePlanLoadResult {
  today: string;
  rows: DischargePlanRow[];
  facilities: DischargeFacilitySummary[];
  /** Who actually left since midnight, over the readable facilities; null =
   *  unknown, which the screen must show as unknown rather than as zero. */
  dischargedToday: number | null;
  /** The unplanned half of that. Absent is kept apart from null on purpose:
   *  absent means the server does not send the field yet, and the screen draws
   *  no tile rather than an unknown one. */
  dischargedTodayUnplanned?: number | null;
  /** Plans left out because their expected date could not be read. Counted,
   *  never silent: the screen says why it is shorter, and the facility counts
   *  are reduced to match. */
  unreadableDates: number;
}

const MS_PER_DAY = 86_400_000;

/** The order the groups render in, soonest first; past due leads because it
 *  is the one that needs a decision today. */
export const BUCKET_ORDER: readonly DischargeBucket[] = ['past', 'today', 'tomorrow', 'week', 'later'];

export const BUCKET_TITLES: Record<DischargeBucket, string> = {
  past: 'Past due',
  today: 'Today',
  tomorrow: 'Tomorrow',
  week: 'Days 3-7',
  later: 'Later',
};

/** YYYY-MM-DD -> a UTC-midnight Date, or null. Never `new Date(iso)`: that
 *  parses as UTC too, but the getters that follow would be local. */
function utcDateOf(iso: string): Date | null {
  if (!ISO_DATE_RE.test(iso || '')) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Whole calendar days from `fromIso` to `toIso` (negative when `toIso` is
 *  earlier). UTC arithmetic, so a DST change between the two cannot make a
 *  day come out as 23 hours and round wrong. */
export function daysBetween(fromIso: string, toIso: string): number {
  const from = utcDateOf(fromIso);
  const to = utcDateOf(toIso);
  if (!from || !to) return 0;
  return Math.round((to.getTime() - from.getTime()) / MS_PER_DAY);
}

export function bucketFor(daysUntil: number): DischargeBucket {
  if (daysUntil < 0) return 'past';
  if (daysUntil === 0) return 'today';
  if (daysUntil === 1) return 'tomorrow';
  // Today and tomorrow have their own bands, so this one is the REMAINDER of
  // the seven-day window (days 3-7 as a reader counts them). The emailed
  // workbook bands the same days, so a plan on day 7 is "Later" on both.
  if (daysUntil <= 6) return 'week';
  return 'later';
}

export function relativeText(daysUntil: number): string {
  if (daysUntil === 0) return 'Today';
  if (daysUntil === 1) return 'Tomorrow';
  if (daysUntil === -1) return 'Yesterday';
  if (daysUntil < 0) return `${-daysUntil} days ago`;
  return `in ${daysUntil} days`;
}

/** "Tue, Sep 9" - rendered in UTC so the day never shifts with the browser. */
export function formatPlanDate(iso: string): string {
  const date = utcDateOf(iso);
  if (!date) return iso || '';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** "M/D/YYYY", the bed board's own short form. */
export function formatPlanDateShort(iso: string): string {
  const date = utcDateOf(iso);
  if (!date) return iso || '';
  return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
}

// Shared with the bed board and High Risk - all three read the same PCC
// columns, so "Unit Unit 4" is solved once. Re-exported as well as used, so
// this service stays the one import site for everything the screen renders.
export { locationText };

export function genderClassOf(gender: string | undefined): GenderClass {
  const g = (gender || '').trim().toLowerCase();
  if (g.startsWith('m')) return 'm';
  if (g.startsWith('f')) return 'f';
  return '';
}

/** The bed board's own rule (BedBoardDataService.getPayerClass), so a payer
 *  cell is the same colour on both screens - including the CCARE reading of
 *  "Medicaid - ..." plans as managed care. */
export function payerClassOf(
  payerType: string | undefined,
  actualPayerType: string | undefined,
  payerName: string | undefined,
): PayerClass {
  const effective = actualPayerType || payerType || '';
  if (effective === 'Medicare A') return 'medicare';
  if (effective === 'Managed Care') return 'managed';
  if (!actualPayerType && environment.snfClient === 'ccare' && (payerName || '').startsWith('Medicaid - ')) return 'managed';
  return 'other';
}

/** "12 days", singular at one, '' when the server sent no length: an unknown
 *  stay is not a stay of no length. */
export function losTextOf(days: number | null | undefined): string {
  if (typeof days !== 'number' || Number.isNaN(days)) return '';
  return days === 1 ? '1 day' : `${days} days`;
}

export function toRow(dto: DischargePlanRowDto, todayIso: string, facilityName: string): DischargePlanRow {
  const daysUntil = daysBetween(todayIso, dto.expectedDischargeDate);
  return {
    ...dto,
    facilityName,
    daysUntil,
    bucket: bucketFor(daysUntil),
    relativeText: relativeText(daysUntil),
    displayDate: formatPlanDate(dto.expectedDischargeDate),
    authoredOnText: easternDateText(dto.updatedAt),
    // The mirror's own column - not user-entered - so it gets the same
    // ISO-date guard expectedDischargeDate gets upstream: a malformed value
    // renders blank, never the raw string formatPlanDateShort would fall back to.
    admittedText: dto.admissionDay && ISO_DATE_RE.test(dto.admissionDay) ? formatPlanDateShort(dto.admissionDay) : '',
    losText: losTextOf(dto.expectedLengthOfStayDays),
    // The first hand on the plan. A server that sent no creator leaves only
    // the last hand to name, and the cell, the sort and the workbook all read
    // this one field - so the fallback lives here, once.
    enteredBy: dto.createdBy?.userName || dto.updatedBy?.userName || '',
    // The date has to come from the SAME hand as the name above: a server
    // with no creator leaves createdAt as whatever moment the document was
    // first inserted, which can predate the last hand by days and would pair
    // the updater's name with someone else's date.
    enteredOnText: easternDateText(dto.createdBy?.userName ? dto.createdAt : dto.updatedAt),
    locationText: locationText(dto.unitDesc, dto.roomDesc, dto.bedDesc),
    roomBed: [dto.roomDesc, dto.bedDesc].filter(Boolean).join('/'),
    genderClass: genderClassOf(dto.gender),
    payerClass: payerClassOf(dto.payerType, dto.actualPayerType, dto.payerName),
    payerText: dto.payerName || dto.payerType || '',
    key: `${dto.facilityId}:${dto.patientId}`,
  };
}

/** Soonest first, then facility name, then patient name - the order the
 *  server sends, re-applied after a local edit moves a row. */
export function sortRows(rows: DischargePlanRow[]): DischargePlanRow[] {
  return [...rows].sort((a, b) =>
    a.expectedDischargeDate.localeCompare(b.expectedDischargeDate)
    || a.facilityName.localeCompare(b.facilityName, undefined, { sensitivity: 'base' })
    || (a.patientName || '').localeCompare(b.patientName || '', undefined, { sensitivity: 'base' }));
}

const compareText = (a: string, b: string): number =>
  (a || '').localeCompare(b || '', undefined, { numeric: true, sensitivity: 'base' });

/** Blanks sink to the bottom whichever way the column is sorted: an empty
 *  payer or author is a gap, not a value that comes "before A". */
function compareOptionalText(a: string, b: string, dir: 1 | -1): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return dir * compareText(a, b);
}

/** The same blanks-last rule for a number: an unknown stay length is a gap,
 *  not the shortest stay. */
function compareOptionalNumber(a: number | null | undefined, b: number | null | undefined, dir: 1 | -1): number {
  const aKnown = typeof a === 'number';
  const bKnown = typeof b === 'number';
  if (!aKnown && !bKnown) return 0;
  if (!aKnown) return 1;
  if (!bKnown) return -1;
  return dir * ((a as number) - (b as number));
}

/**
 * The rows inside every card, by the column the reader picked. Date is the
 * default and the tie-breaker for everything else, so a payer sort still
 * reads soonest-first within each payer.
 */
export function sortRowsBy(rows: DischargePlanRow[], sort: DischargeSort): DischargePlanRow[] {
  const { key, dir } = sort;
  const byDate = (a: DischargePlanRow, b: DischargePlanRow) => a.expectedDischargeDate.localeCompare(b.expectedDischargeDate);
  const byName = (a: DischargePlanRow, b: DischargePlanRow) => compareText(a.patientName, b.patientName);
  return [...rows].sort((a, b) => {
    switch (key) {
      case 'patient':
        return dir * byName(a, b) || byDate(a, b);
      // The building, then the room inside it, so the merged list gathers a
      // facility's rows together and walks its floor in order; in a single
      // building the first key ties and the room does the work.
      case 'facility':
        return compareOptionalText(a.facilityName, b.facilityName, dir)
          || compareOptionalText(a.roomBed, b.roomBed, dir)
          || byDate(a, b) || byName(a, b);
      // The ISO day orders as text because it is zero-padded and big-endian;
      // a row the mirror knows no admission for sinks either way.
      case 'admitted':
        return compareOptionalText(a.admissionDay || '', b.admissionDay || '', dir) || byDate(a, b) || byName(a, b);
      case 'los':
        return compareOptionalNumber(a.expectedLengthOfStayDays, b.expectedLengthOfStayDays, dir) || byDate(a, b) || byName(a, b);
      case 'payer':
        return compareOptionalText(a.payerText, b.payerText, dir) || byDate(a, b) || byName(a, b);
      case 'author':
        return compareOptionalText(a.enteredBy, b.enteredBy, dir) || byDate(a, b) || byName(a, b);
      default:
        return dir * byDate(a, b) || byName(a, b);
    }
  });
}

/** Rows split into the ordered buckets; empty buckets are left out. */
export function groupRows(rows: DischargePlanRow[]): DischargeGroup[] {
  return BUCKET_ORDER
    .map(bucket => ({ bucket, title: BUCKET_TITLES[bucket], rows: rows.filter(r => r.bucket === bucket) }))
    .filter(group => group.rows.length > 0);
}

/** The board's six unit washes, by position: 1..6, then round again. */
export function bandForPosition(position: number): number {
  return (position % 6) + 1;
}

/** Rows split per facility in the index's order (alphabetical), each card
 *  keeping its rows soonest first; facilities with no rows left are out. */
export function groupRowsByFacility(
  rows: DischargePlanRow[],
  index: ReadonlyArray<{ facilityId: number; facilityName: string; band: number; dischargedToday?: number | null }>,
): DischargeFacilityGroup[] {
  // One pass over the rows rather than one per facility: a portfolio holds
  // dozens of buildings and this runs on every keystroke.
  const byFacility = new Map<number, DischargePlanRow[]>();
  for (const row of rows) {
    const bucket = byFacility.get(row.facilityId);
    if (bucket) { bucket.push(row); } else { byFacility.set(row.facilityId, [row]); }
  }
  return index
    .map(f => ({ ...f, dischargedToday: f.dischargedToday ?? null, rows: byFacility.get(f.facilityId) ?? [] }))
    .filter(group => group.rows.length > 0);
}

export function withinHorizon(row: Pick<DischargePlanRow, 'daysUntil'>, horizon: DischargeHorizon): boolean {
  switch (horizon) {
    case 'past': return row.daysUntil < 0;
    // Upper bounds, so these mean the same thing here as in the emailed
    // workbook: the server's horizonLimit is a ceiling only and past-due plans
    // are always included. A reader who narrowed the screen to "Next 7 days"
    // and then received an email configured the same way was getting two
    // different sets, and the overdue rows - the ones that need a decision
    // today - were the ones missing from the screen. The Past due tile stays
    // the way to isolate them.
    case 'today': return row.daysUntil <= 0;
    case '48h': return row.daysUntil <= 1;
    case '7d': return row.daysUntil <= 6;
    default: return true;
  }
}

/** What the tiles and the header strip COUNT under each label. Unlike the row
 *  filter above, the two forward-looking numbers have a floor: "Next 48 hours"
 *  is today and tomorrow, nothing earlier. They are the twin of the emailed
 *  Summary sheet's "Next 48 h" / "Next 7 days" columns (the server's
 *  countPlanBands), which sit beside a separate "Past due" column - the screen
 *  and the workbook must give the same number for the same label, and a
 *  charge nurse staffing from the strip must not read three overdue plans as
 *  three beds turning over by tomorrow. */
export function countedIn(row: Pick<DischargePlanRow, 'daysUntil'>, horizon: DischargeHorizon): boolean {
  switch (horizon) {
    case 'past': return row.daysUntil < 0;
    case 'today': return row.daysUntil === 0;
    case '48h': return row.daysUntil >= 0 && row.daysUntil <= 1;
    case '7d': return row.daysUntil >= 0 && row.daysUntil <= 6;
    default: return true;
  }
}

/** "M/D/YYYY" in America/New_York, whatever the reader's own zone is: the
 *  emailed workbook stamps "Set on" in Eastern, and a plan saved at 22:00 ET
 *  must not read as the next day for a Pacific reader. */
export function easternDateText(value: string | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    timeZone: 'America/New_York', month: 'numeric', day: 'numeric', year: 'numeric',
  });
}

/** The furthest date the popup offers: a year out, the server's own cap. */
export function maxPlanDate(todayIso: string): string {
  const today = utcDateOf(todayIso) ?? new Date();
  const max = new Date(today.getTime() + 366 * MS_PER_DAY);
  return max.toISOString().slice(0, 10);
}

// -- the scheduled email's settings, as drawer cards and back ---------------

const EMAIL_HORIZONS: readonly DischargeEmailHorizon[] = ['all', '48h', '7d', '14d'];

export const DEFAULT_EMAIL_SETTINGS: DischargePlanEmailSettings = {
  facilityIds: [],
  horizon: 'all',
  includeNotes: false,
  layout: 'day',
};

/** The four decisions the drawer paints, in reading order: who, when, how
 *  much, and how it is laid out. Pure - the drawer holds the result and
 *  hands it back on Save. */
export function emailSettingsToSections(settings: DischargePlanEmailSettings): DrawerOptionSection[] {
  const horizon = EMAIL_HORIZONS.indexOf(settings?.horizon) !== -1 ? settings.horizon : DEFAULT_EMAIL_SETTINGS.horizon;
  return [
    {
      kind: 'choice',
      id: 'contents',
      label: 'What the file contains',
      value: settings?.includeNotes ? 'notes' : 'dates',
      choices: [
        { id: 'dates', title: 'Dates and rooms only', subtitle: 'Patient, unit, room, payer and the expected date.' },
        { id: 'notes', title: 'With the reason and who set it', subtitle: 'Adds the note and the name of the person who entered it.' },
      ],
    },

    {
      kind: 'choice',
      id: 'horizon',
      label: 'Time window',
      hint: 'Past-due plans are always included, in their own group.',
      value: horizon,
      choices: [
        { id: 'all', title: 'Everyone with a date' },
        { id: '48h', title: 'Next 48 hours', subtitle: 'Today and tomorrow' },
        { id: '7d', title: 'Next 7 days' },
        { id: '14d', title: 'Next 14 days' },
      ],
    },

    {
      kind: 'choice',
      id: 'layout',
      label: 'Layout',
      value: settings?.layout === 'facility' ? 'facility' : DEFAULT_EMAIL_SETTINGS.layout,
      choices: [
        { id: 'day', title: 'By day, soonest first' },
        { id: 'facility', title: 'One section per facility' },
      ],
    },

    {
      kind: 'facilities',
      id: 'facilities',
      label: 'Which facilities',
      hint: 'Recipients only get the facilities they have access to.',
      value: [...(settings?.facilityIds ?? [])],
    }
  ];
}

export function sectionsToEmailSettings(sections: DrawerOptionSection[]): DischargePlanEmailSettings {
  const find = (id: string): DrawerOptionSection | undefined => (sections ?? []).find(s => s?.id === id);
  const facilities = find('facilities') as DrawerFacilitySection | undefined;
  const choiceOf = (id: string): string => {
    const section = find(id) as DrawerChoiceSection | undefined;
    return section?.kind === 'choice' ? section.value : '';
  };
  const horizon = choiceOf('horizon') as DischargeEmailHorizon;
  const layout = choiceOf('layout') as DischargeView;
  return {
    facilityIds: facilities?.kind === 'facilities'
      // The server rejects the whole payload for anything that is not a
      // positive integer, so a corrupted stored id is dropped here instead.
      ? (facilities.value ?? []).filter(id => Number.isInteger(id) && id > 0)
      : [...DEFAULT_EMAIL_SETTINGS.facilityIds],
    horizon: EMAIL_HORIZONS.indexOf(horizon) !== -1 ? horizon : DEFAULT_EMAIL_SETTINGS.horizon,
    includeNotes: choiceOf('contents') === 'notes',
    layout: layout === 'facility' ? 'facility' : DEFAULT_EMAIL_SETTINGS.layout,
  };
}

@Injectable({ providedIn: 'root' })
export class DischargePlanService {
  constructor(
    private server: ServerTokenService,
    private facilityService: FacilityService,
  ) { }

  /** The bed board's map for one facility, keyed by patient id.
   *  Unreadable dates are dropped here for the same reason load() drops them:
   *  daysBetween() answers 0 for a date it cannot parse, so the row would wear
   *  the amber "needs a hand today" flag with no date beside it. */
  getByFacility(facilityId: number): Observable<DischargePlanByPatientId> {
    return this.server.getWithToken<DischargePlanByPatientId>(`snfs/bedBoardDischargePlan/${facilityId}`)
      .pipe(map(response => {
        const plans: DischargePlanByPatientId = {};
        for (const [patientId, plan] of Object.entries(response ?? {})) {
          if (ISO_DATE_RE.test(plan?.expectedDischargeDate || '')) plans[patientId] = plan;
        }
        return plans;
      }));
  }

  save(input: DischargePlanInput): Observable<DischargePlan> {
    return this.server.postWithToken<DischargePlan>('snfs/bedBoardDischargePlan', input);
  }

  /** Ids in the body, never the URL: every request path is logged. */
  remove(facilityId: number, patientId: string): Observable<{ removed: boolean }> {
    return this.server.deleteWithToken<{ removed: boolean }>('snfs/bedBoardDischargePlan', { facilityId, patientId });
  }

  /** Every plan across the facilities this user may see, flattened for the
   *  screen. The server does the census join; names come from the same
   *  registry the facility dropdown uses. */
  load(): Observable<DischargePlanLoadResult> {
    return this.server
      .postWithToken<DischargePlanListResponseDto>('snfs/getDischargePlans', {})
      .pipe(map(response => {
        const today = response?.today || easternToday();
        // A plan with no usable date has no place on a screen read by day: it
        // would render as "Today" and throw the moment sortRows reads it. It
        // is dropped - and counted per facility, because a chip that promises
        // three rows and a card that holds two is a census the reader stops
        // trusting.
        const droppedPerFacility = new Map<number, number>();
        let unreadableDates = 0;
        const rows: DischargePlanRow[] = [];
        for (const dto of response?.plans ?? []) {
          if (!ISO_DATE_RE.test(dto?.expectedDischargeDate || '')) {
            droppedPerFacility.set(dto?.facilityId, (droppedPerFacility.get(dto?.facilityId) ?? 0) + 1);
            unreadableDates++;
            continue;
          }
          rows.push(toRow(dto, today, this.facilityName(dto.facilityId)));
        }
        // Alphabetical, and the band follows that position: a facility keeps
        // its colour whatever the filters do to the rows.
        const facilities = (response?.facilities ?? [])
          .map(f => ({
            ...f,
            planned: Math.max(0, (f.planned ?? 0) - (droppedPerFacility.get(f.facilityId) ?? 0)),
            dischargedToday: f.dischargedToday ?? null,
            facilityName: this.facilityName(f.facilityId),
            band: 1,
          }))
          .sort((a, b) => a.facilityName.localeCompare(b.facilityName, undefined, { sensitivity: 'base' }))
          .map((f, i) => ({ ...f, band: bandForPosition(i) }));
        return {
          today,
          rows: sortRows(rows),
          facilities,
          dischargedToday: response?.dischargedToday ?? null,
          // Not `?? null`: an absent field is a server without the split, and
          // the screen has to be able to tell that from a census it could not
          // read. Only the second draws a dash.
          dischargedTodayUnplanned: response?.dischargedTodayUnplanned,
          unreadableDates,
        };
      }));
  }

  /** The drawer's option cards, for both hosts (the screen and the admin
   *  inventory). Arrow properties so `this` survives the input hand-off. */
  loadDrawerOptions = (): Observable<DrawerOptionSection[]> =>
    this.getEmailSettings().pipe(map(emailSettingsToSections));

  saveDrawerOptions = (sections: DrawerOptionSection[]): Observable<unknown> =>
    this.saveEmailSettings(sectionsToEmailSettings(sections));

  /** The scheduled email's content settings. Admin-only server-side; the
   *  region comes from the session, never from the body. */
  getEmailSettings(): Observable<DischargePlanEmailSettings> {
    return this.server.postWithToken<DischargePlanEmailSettings>('snfs/getDischargePlanEmailSettings', {});
  }

  saveEmailSettings(settings: DischargePlanEmailSettings): Observable<DischargePlanEmailSettings> {
    return this.server.postWithToken<DischargePlanEmailSettings>('snfs/setDischargePlanEmailSettings', settings);
  }

  facilityName(facilityId: number): string {
    return this.facilityService.getFacilityDisplayName(facilityId) || `Facility ${facilityId}`;
  }
}
